import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { logError } from "@/lib/logError";
import {
  QTY_TIERS,
  initialForm,
  initialAddOns,
  activeTierForCamp,
  computeAddonsTotal,
  hasRequiredFields,
} from "@/components/camps/campConfig";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const addOnPayload = (addOns) => ({
  addon_individual_training_tier: addOns.individual_training.tier,
  addon_individual_training_qty: addOns.individual_training.qty,
  addon_gk_training_tier: addOns.gk_training.tier,
  addon_gk_training_qty: addOns.gk_training.qty,
  addon_analyst_tier: addOns.analyst.tier,
  addon_analyst_qty: addOns.analyst.qty,
});

const feeFor = (camp, tier) => (tier && camp?.fees?.[tier]) || 0;

const nextFeeTier = (prev, slug) =>
  prev.camp_slug === slug && prev.camp_fee_tier ? prev.camp_fee_tier : activeTierForCamp(slug) || "";

const validationError = (form, consent, f) => {
  if (!hasRequiredFields(form)) return f.requiredMissing;
  if (!consent) return f.consentRequired;
  return null;
};

const buildPayload = ({ form, addOns, addonsTotal, campFee, consent, lang }) => ({
  ...form,
  ...addOnPayload(addOns),
  addons_estimate_usd: addonsTotal,
  camp_fee_usd: campFee,
  grand_total_usd: campFee + addonsTotal,
  consent_agreed: consent,
  language: lang,
});

const scrollToForm = () =>
  setTimeout(() => {
    document.getElementById("camp-registration")?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, 50);

export const useCampRegistration = (c, lang) => {
  const f = c.form;
  const [form, setForm] = useState(initialForm);
  const [addOns, setAddOns] = useState(initialAddOns);
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const onChange = (e) => setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  const setTier = (tier) => setForm((prev) => ({ ...prev, camp_fee_tier: tier }));

  const setAddOnTier = (group, tier) =>
    setAddOns((prev) => ({
      ...prev,
      [group]: { tier, qty: QTY_TIERS.includes(tier) ? Math.max(1, prev[group].qty || 1) : 0 },
    }));

  const setAddOnQty = (group, qty) => setAddOns((prev) => ({ ...prev, [group]: { ...prev[group], qty } }));

  const selectedCamp = c.confirmed.find((x) => x.slug === form.camp_slug);
  const campFee = feeFor(selectedCamp, form.camp_fee_tier);
  const addonsTotal = computeAddonsTotal(addOns);

  const chooseCamp = (slug) => {
    setForm((prev) => ({
      ...prev,
      camp_slug: slug,
      camp_fee_tier: nextFeeTier(prev, slug),
    }));
    scrollToForm();
  };

  const reset = () => {
    setForm(initialForm);
    setAddOns(initialAddOns);
    setConsent(false);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const invalid = validationError(form, consent, f);
    if (invalid) return toast.error(invalid);
    setSubmitting(true);
    try {
      await axios.post(`${API}/camp-registrations`, buildPayload({ form, addOns, addonsTotal, campFee, consent, lang }));
      setSubmitted(true);
      toast.success(f.success);
      reset();
    } catch (err) {
      logError("SoccerCamps.submit", err);
      toast.error(f.error);
    } finally {
      setSubmitting(false);
    }
  };

  return {
    form, addOns, consent, submitting, submitted, selectedCamp, campFee, addonsTotal,
    onChange, setTier, setAddOnTier, setAddOnQty, setConsent, setSubmitted, chooseCamp, onSubmit,
  };
};
